import 'assets/css/modal.css';
import 'styles/custom.css';
import * as React from 'react';
import {
  StandardScreen,
  StandardSearchBox,
  StandardButton,
  StatusMessage,
  PageLayout,
  StandardDropdown,
  StandardTextField,
  StandardCheckbox,
} from '../components/Layout';
import { getRegionNames, getCurrentEntraCacheSyncSettings, submitEntraCacheSyncSettings } from "../components/Requests/Requests";

class EntraCacheSync extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: "",
      message: "",
      spHostUrl: "",
      regionNames: [],
      regionNameChosen: "",
      idmSettings: null,
      // editable fields
      id: "",
      idmUrl: "",
      authRealm: "",
      clientId: "",
      secret: "",
      enabled: false,
      idmBaseAuthUrl: "",
      groupScopeEnabled: false,
      groupScopeAttributeName: "",
      groupScopeAttributeValue: "",
      wallsGroupScopeEnabled: false,
      wallsGroupScopeAttributeName: "",
      wallsGroupScopeAttributeValue: "",
      lastSuccessfulSync: "",
      updatedOn: "",
      searchExecuted: false,
      spHostUrlHidden: "",
      submitted: false,
    };
  }

  componentDidMount() {
    this.initializeSettings();
  }

  initializeSettings() {
    this.getRegionNames();
  }

  async getRegionNames() {
    this.setState({ loading: true });
    try {
      const response = await getRegionNames(this.props.accessToken);
      if (response.status === 200) {
        const data = await response.json();
        this.setState({
          loading: false,
          regionNames: data.map(item => ({ key: item, text: item })),
        });
      } else {
        this.setState({ loading: false, error: "Something went wrong" });
      }
    } catch (e) {
      this.setState({ loading: false, error: "Something went wrong" });
    }
  }

  onSelectRegionName = (event, item) => {
    this.setState({ regionNameChosen: item.key, message: "", error: "" });
  };

  onSpHostUrlChange = (newValue) => {
    this.setState({
      spHostUrl: newValue,
      spHostUrlHidden: newValue || "",
      message: "",
      error: "",
    });
  };

  onSearch = async () => {
    this.setState({ loading: true, message: "", error: "", idmSettings: null });
    try {
      const requestBody = { SPHostUrl: this.state.spHostUrl, RegionName: this.state.regionNameChosen };
      const response = await getCurrentEntraCacheSyncSettings(this.props.accessToken, requestBody);
      const data = await response.json();
      if (response.status === 200) {
        this.setState({ 
          idmSettings: data, 
          message: data ? "" : "No IdM settings found",
          spHostUrlHidden: this.state.spHostUrl || "",
          idmUrl: data?.idmUrl || "",
          authRealm: data?.authRealm || "",
          clientId: data?.clientId || "",
          secret: data?.secret || "",
          enabled: data?.enabled || false,
          idmBaseAuthUrl: data?.idmBaseAuthUrl || "",
          groupScopeEnabled: data?.groupScopeEnabled || false,
          groupScopeAttributeName: data?.groupScopeAttributeName || "",
          groupScopeAttributeValue: data?.groupScopeAttributeValue || "",
          wallsGroupScopeEnabled: data?.wallsGroupScopeEnabled || false,
          wallsGroupScopeAttributeName: data?.wallsGroupScopeAttributeName || "",
          wallsGroupScopeAttributeValue: data?.wallsGroupScopeAttributeValue || "",
          lastSuccessfulSync: data?.lastSuccessfulSync || "",
          updatedOn: data?.updatedOn || "",
          searchExecuted: true,
        });
      } else {
        this.setState({ error: "Something went wrong", searchExecuted: true });
      }
    } catch (e) {
      this.setState({ error: 'Something went wrong', searchExecuted: true });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { loading, error, message, spHostUrl, regionNames, regionNameChosen, idmSettings, idmUrl, authRealm, clientId, secret, enabled, idmBaseAuthUrl, groupScopeEnabled, groupScopeAttributeName, groupScopeAttributeValue, wallsGroupScopeEnabled, wallsGroupScopeAttributeName, wallsGroupScopeAttributeValue, lastSuccessfulSync, updatedOn, searchExecuted } = this.state;

    // IDM credentials are only required when IDM is enabled; Walls needs no user-supplied fields.
    const allRequiredFilled = searchExecuted && (!enabled || (idmUrl.trim() !== "" && authRealm.trim() !== "" && clientId.trim() !== "" && secret.trim() !== ""));

    return (
      <PageLayout>
        <StandardScreen
          title="Entra Cache Sync"
          description="Search by SPHost to view Entra Cache Sync details."
          loading={loading}
        >
          <StandardSearchBox
            label="SPHostUrl"
            placeholder="SPHostUrl"
            value={spHostUrl}
            onChange={this.onSpHostUrlChange}
          />

          <StandardDropdown
            label="Environment"
            selectedKey={regionNameChosen}
            onChange={this.onSelectRegionName}
            placeholder="Select Environment"
            options={regionNames}
            disabled={spHostUrl === ""}
          />

          <StandardButton
            text="Search"
            disabled={spHostUrl === "" || regionNameChosen === ""}
            onClick={this.onSearch}
          />

          {message && <StatusMessage>{message}</StatusMessage>}
          {error && <StatusMessage type="error">{error}</StatusMessage>}

          {searchExecuted && (
            <>
              {/* Hidden field to carry SPHostUrl for submission */}
              <input type="hidden" name="SPHostUrl" value={spHostUrl} />
              {!this.state.submitted && (
                <>
                  <StandardCheckbox label="Enable IDM Settings" checked={enabled} onChange={(e, checked) => this.setState({ enabled: !!checked })} />
                  {enabled && (
                    <>
                      <StandardTextField label="IdmUrl" value={idmUrl} onChange={(e, v) => this.setState({ idmUrl: v || "" })} />
                      <StandardTextField label="AuthRealm" value={authRealm} onChange={(e, v) => this.setState({ authRealm: v || "" })} />
                      <StandardTextField label="ClientId" value={clientId} onChange={(e, v) => this.setState({ clientId: v || "" })} />
                      <StandardTextField label="Secret" value={secret} onChange={(e, v) => this.setState({ secret: v || "" })} />
                      <StandardTextField label="Idm Base Auth Url" value={idmBaseAuthUrl} onChange={(e, v) => this.setState({ idmBaseAuthUrl: v || "" })} />
                      <StandardCheckbox label="Group Scope Enabled" checked={groupScopeEnabled} onChange={(e, checked) => this.setState({ groupScopeEnabled: !!checked })} />
                    </>
                  )}
                  {/* IDM and Walls group scope attribute name/value are derived server-side from the region's EntraCacheSync app registration */}
                  <StandardCheckbox label="Enable Walls Settings" checked={wallsGroupScopeEnabled} onChange={(e, checked) => this.setState({ wallsGroupScopeEnabled: !!checked })} />
                  {lastSuccessfulSync && (
                    <StandardTextField label="Last Successful Sync" value={lastSuccessfulSync} readOnly />
                  )}
                  {updatedOn && (
                    <StandardTextField label="Last Updated On" value={updatedOn} readOnly />
                  )}
                </>
              )}
              {!this.state.submitted && (
                <StandardButton
                  text="Submit"
                  disabled={!allRequiredFilled}
                  onClick={async () => {
                    this.setState({ loading: true, error: "", message: "" });
                    try {
                      const requestBody = {
                        SPHostUrl: this.state.spHostUrlHidden || spHostUrl,
                        RegionName: regionNameChosen,
                        IdmUrl: idmUrl,
                        AuthRealm: authRealm,
                        ClientId: clientId,
                        Secret: secret,
                        Enabled: enabled,
                        IdmBaseAuthUrl: idmBaseAuthUrl,
                        GroupScopeEnabled: groupScopeEnabled,
                        GroupScopeAttributeName: groupScopeAttributeName,
                        GroupScopeAttributeValue: groupScopeAttributeValue,
                        WallsGroupScopeEnabled: wallsGroupScopeEnabled,
                        WallsGroupScopeAttributeName: wallsGroupScopeAttributeName,
                        WallsGroupScopeAttributeValue: wallsGroupScopeAttributeValue,
                      };
                      const response = await submitEntraCacheSyncSettings(this.props.accessToken, requestBody);
                      if (response.status === 200) {
                        this.setState({ message: "Settings submitted successfully.", submitted: true });
                      } else {
                        this.setState({ error: "Failed to submit settings" });
                      }
                    } catch (err) {
                      this.setState({ error: "Failed to submit settings" });
                    } finally {
                      this.setState({ loading: false });
                    }
                  }}
                />
              )}
            </>
          )}
        </StandardScreen>
      </PageLayout>
    );
  }
}

export default EntraCacheSync;
